import React, { useState, useEffect, useCallback } from "react";
import { generateClient } from "aws-amplify/data";
import { buildSlides, navigateSlide, Slide } from "../../utils/slideshowUtils";
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import StorageImage from '../StorageImage';
import { formatClubDate } from '../../utils/clubTime';
import { rotatedFrameStyle, rotatedImageStyle } from '../../utils/catchRotation';

const client = generateClient({ authMode: "userPool" });

interface CompetitionSlideshowProps {
  eventId: string;
  onExit: () => void;
}

const CompetitionSlideshow: React.FC<CompetitionSlideshowProps> = ({
  eventId,
  onExit,
}) => {
  const [slides, setSlides] = useState<Slide[]>([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        // Fetch leaderboard
        const lb = await (client.queries as any).calculateLeaderboard({
          eventId,
        });
        const leaderboard = parseLambdaResponse(lb?.data);
        if (!leaderboard?.success)
          throw new Error("Failed to load leaderboard");

        // Fetch catch submissions for photo URLs
        let allCatches: any[] = [];
        let nt: string | null = null;
        do {
          const r: any = await client.models.CatchSubmission.list({
            filter: { eventId: { eq: eventId } },
            ...(nt ? { nextToken: nt } : {}),
          });
          allCatches = allCatches.concat(r.data || []);
          nt = r.nextToken ?? null;
        } while (nt);

        const catchPhotoUrls: Record<string, string | null> = {};
        const catchRotations: Record<string, number | null> = {};
        await Promise.allSettled(
          allCatches
            .filter((c: any) => c.photoS3Key)
            .map(async (c: any) => {
              catchRotations[c.id] = c.displayRotation ?? 0;
              try {
                const r = await (client.queries as any).generateDownloadUrl({
                  s3Key: c.photoS3Key,
                });
                const d = parseLambdaResponse(r?.data);
                if (d?.downloadUrl) catchPhotoUrls[c.id] = d.downloadUrl;
              } catch {
                /* skip */
              }
            }),
        );

        // Fetch happy snaps
        let allPhotos: any[] = [];
        let pt: string | null = null;
        do {
          const r: any = await (client.models as any).CompetitionPhoto.list({
            filter: { eventId: { eq: eventId } },
            ...(pt ? { nextToken: pt } : {}),
          });
          allPhotos = allPhotos.concat(r.data || []);
          pt = r.nextToken ?? null;
        } while (pt);

        const compPhotos: any[] = [];
        await Promise.allSettled(
          allPhotos
            .filter(
              (p: any) => p.status === "APPROVED" || p.status === "WINNER",
            )
            .map(async (p: any) => {
              let photoUrl: string | null = null;
              try {
                const r = await (client.queries as any).generateDownloadUrl({
                  s3Key: p.photoS3Key,
                });
                const d = parseLambdaResponse(r?.data);
                photoUrl = d?.downloadUrl || null;
              } catch {
                /* skip */
              }
              compPhotos.push({
                memberName: p.memberName,
                caption: p.caption || "",
                status: p.status,
                photoUrl,
              });
            }),
        );

        // Fetch sponsors
        let sponsors: any[] = [];
        try {
          const r: any = await client.models.Sponsor.list({
            filter: { isActive: { eq: true } },
          });
          sponsors = (r.data || []).map((s: any) => ({
            name: s.name,
            logoUrl: s.logoUrl || null,
          }));
        } catch {
          /* skip */
        }

        // Fetch event
        const evt: any = await client.models.Event.get({ id: eventId });

        // Fetch registration count
        let registrationCount = 0;
        try {
          let regAll: any[] = [];
          let rt: string | null = null;
          do {
            const r: any = await (
              client.models as any
            ).CompetitionDayRegistration.list({
              filter: { eventId: { eq: eventId } },
              ...(rt ? { nextToken: rt } : {}),
            });
            regAll = regAll.concat(r.data || []);
            rt = r.nextToken ?? null;
          } while (rt);
          registrationCount = regAll.length;
        } catch {
          /* skip */
        }

        // Build overview catch data with photo URLs
        const overviewCatches = allCatches.map((c: any) => ({
          species: c.species,
          status: c.status,
          keptOrReleased: c.keptOrReleased,
          photoUrl: catchPhotoUrls[c.id] || null,
          competitorName: c.competitorName,
          lengthCm: c.lengthCm,
          displayRotation: c.displayRotation ?? 0,
        }));

        setSlides(
          buildSlides(
            {
              title: evt.data?.title || "",
              eventDate: evt.data?.eventDate || "",
            },
            leaderboard,
            catchPhotoUrls,
            compPhotos,
            sponsors,
            { registrationCount, catches: overviewCatches },
            {
              seniorChampionName: evt.data?.seniorChampionName || null,
              seniorChampionPoints: evt.data?.seniorChampionPoints || null,
              juniorChampionName: evt.data?.juniorChampionName || null,
              juniorChampionPoints: evt.data?.juniorChampionPoints || null,
            },
            catchRotations,
          ),
        );
      } catch (err: any) {
        setError(err.message || "Failed to load presentation data");
      }
      setLoading(false);
    };
    load();
  }, [eventId]);

  // Keyboard navigation
  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " ")
        setCurrent((c) => navigateSlide(c, slides.length, "next"));
      if (e.key === "ArrowLeft")
        setCurrent((c) => navigateSlide(c, slides.length, "prev"));
      if (e.key === "Escape") onExit();
    },
    [slides.length, onExit],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  // Fullscreen
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen();
  };

  if (loading)
    return (
      <div className="fixed inset-0 z-[100] bg-gray-900 flex items-center justify-center text-white text-xl">
        Loading presentation…
      </div>
    );
  if (error)
    return (
      <div className="fixed inset-0 z-[100] bg-gray-900 flex flex-col items-center justify-center text-white">
        <p className="text-xl mb-4">{error}</p>
        <button
          onClick={onExit}
          className="px-6 py-3 bg-white text-gray-900 rounded-lg font-medium"
        >
          Exit
        </button>
      </div>
    );
  if (slides.length === 0) return null;

  const slide = slides[current];

  return (
    <div
      className="fixed inset-0 z-[100] bg-gray-900 text-white"
      onClick={() => setCurrent((c) => navigateSlide(c, slides.length, "next"))}
    >
      {/* Controls */}
      <div
        className="absolute top-4 right-4 z-10 flex items-center gap-3"
        onClick={(e: any) => e.stopPropagation()}
      >
        <span className="text-sm text-gray-400">
          {current + 1} / {slides.length}
        </span>
        <button
          onClick={toggleFullscreen}
          className="px-3 py-1 bg-gray-700 rounded text-sm hover:bg-gray-600"
        >
          {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
        </button>
        <button
          onClick={onExit}
          className="px-3 py-1 bg-red-600 rounded text-sm hover:bg-red-700"
        >
          Exit
        </button>
      </div>

      {/* Slide content */}
      <div className="h-full flex items-center justify-center p-8">
        {slide.type === "title" && (
          <div className="text-center">
            <div className="text-6xl mb-6">🎣</div>
            <h1 className="text-5xl font-bold mb-4">{slide.eventTitle}</h1>
            <p className="text-2xl text-gray-300 mb-8">
              {formatClubDate(slide.eventDate, {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
            <div className="flex gap-12 justify-center text-xl">
              <span>{slide.totalCatches} catches</span>
              <span>{slide.uniqueCompetitors} competitors</span>
            </div>
          </div>
        )}

        {slide.type === "event-overview" && (
          <div className="text-center w-full max-w-5xl">
            <h2 className="text-4xl font-bold mb-8">📊 Event Overview</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-8">
              <div>
                <div className="text-4xl font-bold text-blue-400">
                  {slide.registrationCount}
                </div>
                <div className="text-sm text-gray-400">Registrations</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-green-400">
                  {slide.totalCatches}
                </div>
                <div className="text-sm text-gray-400">Total Catches</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-yellow-400">
                  {slide.keptCount}
                </div>
                <div className="text-sm text-gray-400">Kept</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-cyan-400">
                  {slide.releasedCount}
                </div>
                <div className="text-sm text-gray-400">Released</div>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {Object.entries(slide.speciesCounts).map(([species, count]) => (
                <span
                  key={species}
                  className="px-3 py-1 bg-gray-700 rounded-full text-sm"
                >
                  {species}: {count}
                </span>
              ))}
            </div>
            {slide.highlightPhotos.length > 0 && (
              <div className="flex justify-center gap-4">
                {slide.highlightPhotos.map((p, i) => (
                  <div key={i} className="text-center">
                    <div style={rotatedFrameStyle(p.displayRotation)} className="w-36 h-36 overflow-hidden rounded-lg mb-2">
                      <img
                        src={p.photoUrl}
                        alt={p.competitorName}
                        className="w-36 h-36 object-cover"
                        style={rotatedImageStyle(p.displayRotation)}
                      />
                    </div>
                    <div className="text-sm">{p.competitorName}</div>
                    <div className="text-xs text-gray-400">
                      {p.species} — {p.lengthCm}cm
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {slide.type === "species-winners" && (
          <div className="text-center w-full max-w-4xl">
            <h2 className="text-4xl font-bold mb-8">{slide.speciesDisplay}</h2>
            <div className="flex justify-center gap-8">
              {slide.winners.map((w: any) => (
                <div
                  key={w.rank}
                  className={`flex flex-col items-center ${w.rank === 1 ? "order-2 scale-110" : w.rank === 2 ? "order-1" : "order-3"}`}
                >
                  {w.photoUrl ? (
                    <div style={rotatedFrameStyle(w.displayRotation)} className="w-40 h-40 overflow-hidden rounded-lg mb-3">
                      <img
                        src={w.photoUrl}
                        alt={w.name}
                        className="w-40 h-40 object-cover"
                        style={rotatedImageStyle(w.displayRotation)}
                      />
                    </div>
                  ) : (
                    <div className="w-40 h-40 bg-gray-700 rounded-lg mb-3 flex items-center justify-center text-5xl">
                      🐟
                    </div>
                  )}
                  <div
                    className={`text-2xl font-bold mb-1 ${w.rank === 1 ? "text-yellow-400" : w.rank === 2 ? "text-gray-300" : "text-orange-400"}`}
                  >
                    {w.rank === 1 ? "🥇" : w.rank === 2 ? "🥈" : "🥉"}{" "}
                    {w.rank === 1 ? "1st" : w.rank === 2 ? "2nd" : "3rd"}
                  </div>
                  <div className="text-xl font-semibold">{w.name}</div>
                  <div className="text-lg text-gray-400">{w.lengthCm}cm</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {slide.type === "biggest-fish" && (
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-2 text-yellow-400">
              🏆 Biggest Fish of the Day
            </h2>
            {slide.photoUrl ? (
              <div style={rotatedFrameStyle(slide.displayRotation)} className="w-64 h-64 overflow-hidden rounded-lg mx-auto mb-4">
                <img
                  src={slide.photoUrl}
                  alt={slide.name}
                  className="w-64 h-64 object-cover"
                  style={rotatedImageStyle(slide.displayRotation)}
                />
              </div>
            ) : (
              <div className="w-64 h-64 bg-gray-700 rounded-lg mx-auto mb-4 flex items-center justify-center text-7xl">
                🐟
              </div>
            )}
            <div className="text-4xl font-bold">{slide.name}</div>
            <div className="text-2xl text-gray-300">
              {slide.speciesDisplay} — {slide.lengthCm}cm
            </div>
          </div>
        )}

        {slide.type === "day-champions" && (
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-8 text-yellow-400">🏆 Day's Champions</h2>
            <div className="flex justify-center gap-16">
              <div>
                <h3 className="text-2xl text-gray-400 mb-3">Senior Champion</h3>
                {slide.seniorChampionName ? (
                  <>
                    <div className="text-4xl font-bold">{slide.seniorChampionName}</div>
                    <div className="text-2xl text-yellow-400 mt-2">{slide.seniorChampionPoints} pts</div>
                  </>
                ) : (
                  <div className="text-2xl text-gray-500 italic">To be confirmed</div>
                )}
              </div>
              <div>
                <h3 className="text-2xl text-gray-400 mb-3">Junior Champion</h3>
                {slide.juniorChampionName ? (
                  <>
                    <div className="text-4xl font-bold">{slide.juniorChampionName}</div>
                    <div className="text-2xl text-yellow-400 mt-2">{slide.juniorChampionPoints} pts</div>
                  </>
                ) : (
                  <div className="text-2xl text-gray-500 italic">To be confirmed</div>
                )}
              </div>
            </div>
          </div>
        )}

        {slide.type === "happy-snaps-section" && (
          <div className="text-center">
            <div className="text-7xl mb-6">📸</div>
            <h2 className="text-5xl font-bold">Happy Snaps</h2>
            <p className="text-2xl text-gray-400 mt-4">
              Best moments from the day
            </p>
          </div>
        )}

        {slide.type === "happy-snap-winner" && (
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-4 text-yellow-400">
              🏆 Best Photo
            </h2>
            {slide.photoUrl ? (
              <img
                src={slide.photoUrl}
                alt={slide.memberName}
                className="max-h-[60vh] rounded-lg mx-auto mb-4"
              />
            ) : (
              <div className="w-64 h-64 bg-gray-700 rounded-lg mx-auto mb-4 flex items-center justify-center text-6xl">
                📷
              </div>
            )}
            <div className="text-2xl font-semibold">{slide.memberName}</div>
            {slide.caption && (
              <div className="text-lg text-gray-400 mt-2 italic">
                "{slide.caption}"
              </div>
            )}
          </div>
        )}

        {slide.type === "happy-snaps-gallery" && (
          <div className="w-full max-w-5xl">
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
              {slide.photos.map((p: any, i: number) => (
                <div key={i}>
                  {p.photoUrl ? (
                    <img
                      src={p.photoUrl}
                      alt={p.memberName}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-full h-32 bg-gray-700 rounded-lg flex items-center justify-center text-3xl">
                      📷
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {slide.type === "closing" && (
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-4">
              Thanks for a great day! 🎣
            </h2>
            <p className="text-xl text-gray-400 mb-8">
              See you at the next competition
            </p>
            {slide.sponsors.length > 0 && (
              <>
                <p className="text-lg text-gray-500 mb-4">
                  Thanks to our sponsors
                </p>
                <div className="flex flex-wrap justify-center gap-6">
                  {slide.sponsors.map((s: any) => (
                    <div key={s.name} className="flex flex-col items-center">
                      {s.logoUrl ? (
                        <StorageImage
                          storageKey={s.logoUrl}
                          alt={s.name}
                          className="h-16 w-auto object-contain"
                          fallback={<span className="text-sm text-gray-400">{s.name}</span>}
                        />
                      ) : (
                        <span className="text-sm text-gray-400">{s.name}</span>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetitionSlideshow;
